import type { ExportDeidentifiedHouseholdOcrQuery } from './graphql/ClientImportExport.generated';

type OcrHousehold = Extract<
  ExportDeidentifiedHouseholdOcrQuery['household'],
  { __typename?: 'Household' }
>;

// ASCII names keep USTAR's byte limits independent of the original name's encoding.
function safeName(name: string): string {
  return name
    .replace(/[^a-zA-Z0-9\s_-]/g, '')
    .replace(/[\s_-]+/g, '_')
    .replace(/^_|_$/g, '')
    .toLowerCase();
}

export function getSafeOcrHouseholdName(id: string): string {
  return safeName(id).slice(0, 100) || 'household';
}

function tarEntry(folder: string, filename: string, content: string): Blob {
  const encoder = new TextEncoder();
  const bytes = encoder.encode(content);
  const header = new Uint8Array(512);
  function write(offset: number, value: string) {
    header.set(encoder.encode(value), offset);
  }
  function octal(offset: number, width: number, value: number) {
    const digits = value.toString(8);
    if (digits.length >= width) {
      throw new Error(
        'Deidentified OCR artifact exceeds the USTAR size limit.'
      );
    }
    write(offset, digits.padStart(width - 1, '0') + '\0');
  }

  // Use USTAR's prefix field so the folder and filename have separate limits.
  write(0, filename);
  octal(100, 8, 0o644);
  octal(108, 8, 0);
  octal(116, 8, 0);
  octal(124, 12, bytes.length);
  octal(136, 12, 0);
  write(148, '        ');
  write(156, '0');
  write(257, 'ustar\0');
  write(263, '00');
  write(345, folder);
  const checksum = header.reduce((sum, byte) => sum + byte, 0);
  write(148, checksum.toString(8).padStart(6, '0') + '\0 ');

  return new Blob([
    header,
    bytes,
    new Uint8Array((512 - (bytes.length % 512)) % 512),
  ]);
}

export async function createDeidentifiedOcrArchive(
  household: OcrHousehold
): Promise<Blob> {
  const documents = (household.documents ?? []).filter(
    (doc) =>
      doc.deidentifiedOcrExtractedMarkdownContent?.trim() ||
      doc.deidentifiedAnnotatedDocumentContent?.trim()
  );
  if (!documents.length) {
    throw new Error(
      'This household has no non-empty deidentified OCR artifacts.'
    );
  }

  const folder = `${getSafeOcrHouseholdName(household.id)}_ocr_artifacts`;
  // Leave space for numeric collision suffixes and the longest artifact extension.
  const namedDocuments = documents.map((doc, index) => ({
    doc,
    base:
      safeName(doc.name.replace(/\.[^/.]+$/, '')).slice(0, 70) ||
      `document_${index + 1}`,
  }));
  const counts = new Map<string, number>();
  for (const { base } of namedDocuments)
    counts.set(base, (counts.get(base) ?? 0) + 1);
  const usedNames = new Set<string>();
  const entries: Blob[] = [];
  namedDocuments.forEach(({ doc, base }) => {
    let filename = base;
    let suffix = 0;
    while (
      usedNames.has(filename) ||
      (suffix === 0 && (counts.get(base) ?? 0) > 1) ||
      (suffix > 0 && counts.has(filename))
    ) {
      suffix += 1;
      filename = `${base}_${suffix}`;
    }
    usedNames.add(filename);
    if (doc.deidentifiedOcrExtractedMarkdownContent?.trim()) {
      entries.push(
        tarEntry(
          folder,
          `${filename}.md`,
          doc.deidentifiedOcrExtractedMarkdownContent
        )
      );
    }
    if (doc.deidentifiedAnnotatedDocumentContent?.trim()) {
      entries.push(
        tarEntry(
          folder,
          `${filename}_ocr_content.txt`,
          doc.deidentifiedAnnotatedDocumentContent
        )
      );
    }
  });

  const tar = new Blob([...entries, new Uint8Array(1024)]);
  const compressed = tar.stream().pipeThrough(new CompressionStream('gzip'));
  return new Blob([await new Response(compressed).arrayBuffer()], {
    type: 'application/gzip',
  });
}
